import { useSelector } from 'react-redux';
import { RootState } from 'redux/store';
import { getOrderAmountErrorMessage } from './getOrderAmountErrorMessage';
import { OrderAmountErrorProps } from './types';

const calculateSumToBePaid = ({
  totalPrice,
  unitPrice,
  unitVolume,
  useFastWithdraw,
  commissionPercent,
  commissionMinAmount,
  useTotalPriceOnly = false,
}: {
  totalPrice: number;
  unitPrice: number;
  unitVolume: number;
  useFastWithdraw: boolean;
  commissionPercent: number;
  commissionMinAmount: number;
  useTotalPriceOnly?: boolean;
}) => {
  const baseAmount = useTotalPriceOnly ? totalPrice : unitPrice * unitVolume;
  const sumWithPercent = baseAmount * (commissionPercent / 100);

  let finalCommission: number;
  if (useFastWithdraw && baseAmount) {
    const fastWithdrawRate = (commissionPercent + 1) / 100;
    const fastWithdrawFee = baseAmount * fastWithdrawRate;
    finalCommission = Math.max(fastWithdrawFee, 100);
  } else {
    finalCommission =
      sumWithPercent < commissionMinAmount ? commissionMinAmount : sumWithPercent;
  }

  return totalPrice ? (totalPrice + finalCommission).toFixed(2) : 0;
};

export const useOrderAmountError = ({
  unitPrice,
  totalPrice,
  unitVolume,
  useFastWithdraw = false,
  amountError,
  isUnitBased = false,
  useTotalPriceOnly = false,
}: Omit<OrderAmountErrorProps, 'className'>) => {
  const { balance, infoPercent, min_order_amount, max_order_amount } = useSelector(
    (state: RootState) => state.authSlice,
  );

  const normalizedTotalPrice = Number(totalPrice) || 0;

  const platformLimitError =
    (min_order_amount > normalizedTotalPrice ||
      max_order_amount < normalizedTotalPrice) &&
    !!normalizedTotalPrice;

  const sumToBePaid = calculateSumToBePaid({
    totalPrice,
    unitPrice,
    unitVolume,
    useFastWithdraw,
    commissionPercent: infoPercent.commission_percent,
    commissionMinAmount: infoPercent.commission_min_amount,
    useTotalPriceOnly,
  });

  const totalAmountToPay = typeof sumToBePaid === 'string' ? parseFloat(sumToBePaid) : 0;
  const insufficientBalanceError =
    !!balance && totalAmountToPay > 0 && +balance < totalAmountToPay;

  const message = getOrderAmountErrorMessage({
    balance,
    insufficientBalanceError,
    amountError,
    platformLimitError,
    min: min_order_amount,
    max: max_order_amount,
    isUnitBased: isUnitBased,
  });

  return {
    message,
    sumToBePaid,
  };
};
